import { Table } from "antd";
import {
  MeasurementTank,
  RequestEditInventoryControlRegister,
  RequestGetInventoryControlDetails,
} from "api/generated";
import moment from "moment";
import { Fragment, useMemo, useState } from "react";
import { InventoryControlCauseInconsistencyForm } from "./InventoryControlCauseInconsistencyForm";
import InventoryControlDetails from "./InventoryControlDetails";
import {
  DailyStockControlData,
  getStockControlColumns,
} from "./StockControlColumns";
import { useIsMobile } from "hooks/useIsMobile";

interface StockControlTableProps {
  registries: RequestGetInventoryControlDetails[];
  currentPage: number;
  pageSize: number;
  totalItems: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  recalculateGic: (
    dateToReCalculate: moment.Moment,
    desiredInitialVolume?: number
  ) => void;
  upsertCauseAndSolutionOnDifference: (
    request: RequestEditInventoryControlRegister
  ) => void;
  getLastMeasurement: (
    wareHouseInventoryControlId: string
  ) => Promise<MeasurementTank[]>;
  onRegisterManualMeasurement?: (record: moment.Moment) => void;
}

function StockControlTable({
  registries,
  currentPage,
  pageSize,
  totalItems,
  loading,
  onPageChange,
  onPageSizeChange,
  recalculateGic,
  upsertCauseAndSolutionOnDifference,
  getLastMeasurement,
  onRegisterManualMeasurement,
}: StockControlTableProps) {
  const { isMobile } = useIsMobile();
  const [isCauseRegisterOpen, setIsCauseRegisterOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [allowEdit, setAllowEdit] = useState(false);
  const [gicRecord, setGicRecord] = useState<DailyStockControlData>();

  const columnActions = {
    onViewCause: (record: DailyStockControlData) => {
      setAllowEdit(false);
      setIsCauseRegisterOpen(true);
      setGicRecord(record);
    },
    onEditCause: (record: DailyStockControlData) => {
      setAllowEdit(true);
      setIsCauseRegisterOpen(true);
      setGicRecord(record);
    },
    onRecalculate: (record: DailyStockControlData) => {
      recalculateGic(moment(record.gicDate));
    },
    onViewDetails: (record: DailyStockControlData) => {
      setIsDetailsOpen(true);
      setGicRecord(record);
    },
    onRegisterManualMeasurement: (record: DailyStockControlData) => {
      onRegisterManualMeasurement?.(moment(record.gicDate));
    },
    showActions: true,
    isMobile,
  };

  const columns = getStockControlColumns(columnActions);

  const dataSource = useMemo(() => {
    return registries.map((item) => {
      const fecha = moment(item.inventoryControlDetailsRegisteredDate);
      const registry: DailyStockControlData = {
        id: item.inventoryControlDetailsUUID ?? "",
        key: item.inventoryControlDetailsSequence ?? "",
        pdd: item.distributionPointName ?? "",
        gicDate: fecha,
        initialVolume: item.inventoryControlDetailsDailyInitialVolume,
        inputs: item.inventoryControlDetailsDailyInputs,
        sales: item.inventoryControlDetailsDailySales,
        calculatedVolume: item.inventoryControlDetailsDailyFinalVolume,
        inventoryVolume: item.inventoryControlDetailsDailyStaticVolume,
        differenceLitters: item.inventoryControlDetailsVolumeDifferenceLiters,
        differencePercentage: Number(
          item.inventoryControlDetailsVolumeDifference
        ),
        isEditable: item.inventoryControlDetailsIsEditable ?? false,
        isEdited: item.inventoryControlDetailsIsEdited ?? false,
        hasArrivingLateInformation: item.hasArrivingLateInformation ?? false,
        wic: item.containerInventoryControl ?? [],
        differenceReason: item.inventoryControlDetailsVolumeDifferenceReason,
        differenceSolution:
          item.inventoryControlDetailsVolumeDifferenceSolution,
      };
      return registry;
    });
  }, [registries]);

  return (
    <Fragment>
      <Table<DailyStockControlData>
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "15", "31", "50", "100"],
          showTotal: (total) => `Total ${total} elementos`,
          pageSize: pageSize,
          current: currentPage,
          total: totalItems,
          position: ["bottomLeft"],
          onChange: (page) => {
            onPageChange(page);
          },
          onShowSizeChange(_, size) {
            onPageSizeChange(size);
          },
        }}
      />

      <InventoryControlCauseInconsistencyForm
        isOpen={isCauseRegisterOpen}
        onClose={() => {
          setIsCauseRegisterOpen(false);
          setGicRecord(undefined);
        }}
        record={gicRecord}
        allowEdit={allowEdit}
        onSubmit={(values) => {
          upsertCauseAndSolutionOnDifference(values);
        }}
        loading={loading}
      />
      <InventoryControlDetails
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setGicRecord(undefined);
        }}
        record={gicRecord}
        getLastMeasurement={getLastMeasurement}
      />
    </Fragment>
  );
}

export default StockControlTable;
